// UpdateBanner.tsx (Story 7.5 / #420) — the opt-in "new version available — reload" prompt.
//
// After a deploy the Story 7.2 Service Worker keeps serving the OLD app shell (it deliberately
// does not skipWaiting()), so a plain F5 shows stale UI until every tab closes. This banner is the
// opt-in fix: when register-sw detects a waiting update, it shows a small, dismissible, non-blocking
// banner; clicking Reload posts {type:"franklin-skip-waiting"} to the waiting worker and reloads
// once it takes control. It NEVER auto-reloads, and it is SUPPRESSED while a scoring evaluation is
// active (the `suppressed` prop) so an in-progress evaluation is never interrupted — the clinician
// opts in between evaluations. Orthogonal to conflict resolution (group G); shares no files with it.

import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import {
  applyServiceWorkerUpdate,
  getServiceWorkerUpdateState,
  subscribeServiceWorkerUpdate,
  type ServiceWorkerUpdateState,
} from "@/lib/register-sw";

export interface UpdateBannerProps {
  /**
   * Hold the banner while a scoring evaluation is active (AC21) — an in-progress evaluation must
   * never be interrupted by an app reload. The update signal persists, so the banner appears once
   * suppression lifts (between evaluations). The mounting surface (the eval shell, Story 6.7/6.9)
   * passes `true` while scoring; default `false` shows it as soon as an update is detected.
   */
  suppressed?: boolean;
  /** Extra classes for the rendered banner — lets the mounting shell space it on the grid (#420). */
  className?: string;
}

/** A dismissible, non-blocking banner prompting an opt-in reload when a new app version is ready. */
export function UpdateBanner({ suppressed = false, className }: UpdateBannerProps) {
  const [state, setState] = useState<ServiceWorkerUpdateState>(getServiceWorkerUpdateState);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Re-read on subscribe to catch an update emitted between the initial snapshot and this
    // subscription (mount-vs-emit race).
    setState(getServiceWorkerUpdateState());
    return subscribeServiceWorkerUpdate(setState);
  }, []);

  // A fresh waiting worker (e.g. a second deploy this session) re-surfaces the banner even after a
  // previous prompt was dismissed.
  useEffect(() => setDismissed(false), [state.waiting]);

  // Hold while an evaluation is active, after a dismiss, or until an update actually exists.
  if (!state.updateAvailable || dismissed || suppressed) return null;

  return (
    <Banner
      variant="info"
      role="status"
      className={["flex-row items-center gap-3", className].filter(Boolean).join(" ")}
      data-testid="update-banner"
    >
      <span className="text-[color:var(--text-primary)]">
        A new version is available. Reload to update.
      </span>
      <div className="ml-auto flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={() => applyServiceWorkerUpdate(state.waiting)}
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Reload
        </Button>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Dismiss update notification"
          onClick={() => setDismissed(true)}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </Banner>
  );
}
